/**
 * @fileoverview Utilidades para la gestión y serialización de conversaciones en Mercately CRM.
 * Proporciona funciones para obtener información relevante de conversaciones, agentes y estados de tickets.
 * @module conversationUtils
 * @category Utils
 * @since 2.0.0
 * @version 3.1.0
 */

/**
 * @typedef {Object} Conversation
 * @description Datos puros de la conversación antes de cualquier transformación o serialización.
 * @property {string|number} id - Identificador único de la conversación.
 * @property {string} [first_name] - Nombre del cliente.
 * @property {string} [last_name] - Apellido del cliente.
 * @property {string} [phone] - Teléfono del cliente.
 * @property {string} [email] - Email del cliente.
 * @property {string} [whatsapp_name] - Nombre en WhatsApp.
 * @property {Object} [assigned_agent] - Agente asignado, contiene al menos el id.
 * @property {string|number} [assigned_agent.id] - ID del agente asignado.
 * @property {boolean} [buy_intention] - Indica si hay intención de compra.
 * @property {boolean} [generated_order] - Indica si se generó una orden.
 * @property {boolean} [has_deals] - Indica si hay deals asociados.
 * @property {boolean} [active_bot] - Indica si hay bot activo.
 * @property {boolean} [allow_mia_chatbot] - Indica si se permite el chatbot Mia.
 * @property {boolean} [mia_chatbot_active] - Indica si el chatbot Mia está activo.
 * @property {boolean} [human_help] - Indica si se solicitó ayuda humana.
 * @property {string|number} [id_in_shops] - ID en tiendas integradas.
 * @property {string|number} [customer_id] - ID del cliente.
 * @property {string|number} [web_id] - ID web del cliente.
 * @property {boolean} [has_unread_messages] - Alternativa para mensajes no leídos.
 * @property {string} [platform] - Plataforma de la conversación.
 * @property {string} [recent_message_date] - Fecha del último mensaje.
 * @property {string} [status_chat] - Estado del chat/ticket.
 * @property {Array<string>} [tags] - Etiquetas asociadas.
 * @property {number} [unread_messages_count] - Cantidad de mensajes no leídos.
 * @property {boolean} [your_turn] - Indica si es turno del usuario.
 * @property {string} [recent_inbound_message_date] - Fecha del último mensaje entrante.
 * @property {boolean} [blocked] - Indica si el usuario está bloqueado.
 */

/**
 * @typedef {Object} SerializedConversation
 * @description Objeto serializado de la conversación, listo para uso en UI y lógica de negocio.
 * @property {string|number} id - Identificador único de la conversación.
 * @property {Object} assignedAgent - Información del agente asignado.
 * @property {string|number|null} assignedAgent.id - ID del agente asignado (puede ser null).
 * @property {string} assignedAgent.fullName - Nombre completo o email del agente asignado.
 * @property {boolean} buyIntentionLabel - Indica si hay intención de compra.
 * @property {string} fullName - Nombre completo del cliente (procesado).
 * @property {string} phone - Teléfono visible del cliente (enmascarado si no hay permiso).
 * @property {string} contactPhone - Teléfono crudo del contacto (sin enmascarar; para payloads operativos).
 * @property {string} email - Email del cliente.
 * @property {boolean} generatedMiaOrderLabel - Indica si se generó una orden Mia.
 * @property {boolean} hasDeals - Indica si hay deals asociados.
 * @property {boolean} hasFlow - Indica si hay flujo/bot activo.
 * @property {boolean} hasAllowFlow - Indica si se permite el flujo/bot.
 * @property {boolean} hasMia - Indica si se permite el chatbot Mia.
 * @property {boolean} hasAllowMia - Indica si se permite o está activo el chatbot Mia.
 * @property {boolean} hasShopOrder - Indica si hay orden en tiendas integradas.
 * @property {boolean} humanHelpLabel - Indica si se solicitó ayuda humana.
 * @property {string|number} customerId - ID del cliente.
 * @property {string|number} customerWebId - ID web del cliente.
 * @property {boolean} isUnRead - Indica si hay mensajes no leídos.
 * @property {boolean} isComment - Indica si es comentario de Instagram o Facebook.
 * @property {string} lastMessageAt - Fecha del último mensaje.
 * @property {string} platform - Plataforma de la conversación.
 * @property {string|number} statusChat - Estado del chat/ticket.
 * @property {Array<string>} tags - Etiquetas asociadas.
 * @property {string} ticketStatus - Letra del estado del ticket.
 * @property {number} unreadCount - Cantidad de mensajes no leídos.
 * @property {boolean} yourTurnLabel - Indica si es turno del usuario.
 * @property {boolean} userIsBlocked - Indica si el usuario está bloqueado.
 * @property {string} recentInboundMessageDate - Fecha del último mensaje entrante.
 * @property {string|null} platformRetailerName - Nombre de la página o canal asociado.
 * @property {boolean} platformRetailerDisconnected - Indica si la integración del canal fue desconectada (chat en solo lectura).
 * @property {string|null} externalUserId - Identificador único del usuario en la plataforma externa.
 * @property {string|null} country - Código ISO2 del país del contacto (ej. "EC", "MX"), calculado por el microservicio inbox.
 */

import { customerFullName } from "../../util/utils";
import STATUS_CHAT_MAP, { getStatusNumber } from "./statusChatMap";
import { visiblePhoneNumber } from "../../util/phoneNumber";
import { resolveInboxConversationPhone } from "../../services/inbox/resolveInboxConversationPhone";

/**
 * Verifica si un objeto está vacío.
 * @function isEmpty
 * @param {Object} obj - Objeto a verificar.
 * @returns {boolean} Verdadero si el objeto está vacío o nulo.
 * @example
 * isEmpty({}) // true
 * isEmpty({ a: 1 }) // false
 * @since 2.0.0
 * @version 3.1.0
 */
export const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;

/**
 * Obtiene la letra que representa el estado del ticket de una conversación.
 * @function getTicketStatus
 * @param {Conversation} conversation - Conversación a analizar.
 * @returns {string} Letra del estado del ticket ('N' si no existe).
 * @example
 * getTicketStatus({ status_chat: 'open' }) // 'O'
 * @see STATUS_CHAT_MAP
 * @since 2.0.0
 * @version 3.1.0
 */
export const getTicketStatus = (conversation) => {
  let statusChat = conversation.status_chat;
  if (typeof statusChat !== 'number') {
    statusChat = getStatusNumber(statusChat);
  }
  return STATUS_CHAT_MAP[statusChat]?.letter || 'N';
};

/**
 * Obtiene el nombre completo del agente asignado.
 * @function agentName
 * @param {string|number} agentId - ID del agente.
 * @param {Object} inboxAgentsReducer - Estado del reducer de agentes.
 * @returns {string} Nombre completo o email del agente, o 'No asignado'.
 * @example
 * agentName(1, { inboxAgentList: [{ id: 1, first_name: 'Ana', last_name: 'Pérez' }] }) // 'Ana Pérez'
 * @since 2.0.0
 * @version 3.1.0
 */
export const agentName = (agentId, inboxAgentsReducer) => {
  if (!agentId) return 'No asignado';
  const agent = inboxAgentsReducer.inboxAgentList.find((_agent) => _agent.id === agentId);
  if (!agent) return 'No asignado';
  if (typeof agent.first_name === 'string' && agent.first_name.trim() && typeof agent.last_name === 'string' && agent.last_name.trim()) {
    return `${agent.first_name} ${agent.last_name}`;
  }
  if (typeof agent.email === 'string' && agent.email.trim()) return agent.email;
  return 'No asignado';
};

/**
 * Verifica si la conversación corresponde a un comentario en redes sociales.
 * @function isComment
 * @param {Conversation} conversation - Conversación a analizar.
 * @returns {boolean} Verdadero si es comentario de Instagram o Facebook.
 * @example
 * isComment({ platform: 'instagram_comments' }) // true
 * @since 2.0.0
 * @version 3.1.0
 */
export const isComment = (conversation) => conversation.platform === 'instagram_comments'
  || conversation.platform === 'facebook_comments'
  || conversation.platform === 'tik_tok_comments';

/**
 * Obtiene el número de statusChat a partir de la conversación, mapeando por status, status_interaction o status_chat.
 * @function getStatusChatNumber
 * @param {Object} conv - Objeto de conversación (ya desestructurado).
 * @returns {number} Número de statusChat correspondiente.
 */
export const getStatusChatNumber = (conv) => {
  let statusChatNum = conv.status_chat;
  if (typeof statusChatNum !== 'number') {
    statusChatNum = getStatusNumber(statusChatNum);
  }
  return Number(statusChatNum);
};

/**
 * Serializa una conversación para su uso en la interfaz de usuario.
 * @function serializeConversation
 * @param {Object} conversation - Objeto de conversación.
 * @param {Object} currentRetailerUser - Usuario actual del minorista.
 * @param {Object} inboxAgentsReducer - Estado del reducer de agentes.
 * @returns {Object} Conversación serializada.
 */
export const serializeConversation = (conversation, currentRetailerUser, inboxAgentsReducer, options = {}) => {
  const conv = { ...conversation?.attributes };
  const contactPhone = resolveInboxConversationPhone({
    phone: conv.phone,
    contactPhone: conv.contactPhone
  }) || '';
  return {
    id: conv.id ?? conversation.id,
    assignedAgent: {
      id: conv.assigned_agent?.id || null,
      fullName: agentName(conv.assigned_agent?.id, inboxAgentsReducer)
    },
    buyIntentionLabel: !!conv.buy_intention,
    fullName: customerFullName({ ...conv, phone: contactPhone || conv.phone }, currentRetailerUser),
    phone: visiblePhoneNumber(contactPhone, currentRetailerUser),
    contactPhone,
    email: conv.email,
    generatedMiaOrderLabel: !!conv.generated_order,
    hasDeals: !!conv.has_deals,
    hasFlow: !!conv.active_bot,
    hasAllowFlow: !!conv.active_bot || !!conv.allow_start_bots,
    hasMia: !!conv.mia_chatbot_active,
    hasAllowMia: !!conv.allow_mia_chatbot || !!conv.mia_chatbot_active,
    hasShopOrder: !!conv.id_in_shops,
    humanHelpLabel: !!conv.human_help,
    customerId: conv.customer_id,
    customerWebId: conv.web_id,
    isUnRead: !!conv.has_unread_messages,
    isComment: isComment(conv),
    lastMessageAt: conv.recent_message_date,
    platform: conv.platform,
    platformRetailerName: conv.platform_retailer_name,
    platformRetailerId: conv.platform_retailer_id || null,
    platformRetailerDisconnected: !!conv.platform_retailer_disconnected,
    statusChat: getStatusChatNumber(conv),
    tags: conv.tags,
    ticketStatus: getTicketStatus(conv),
    unreadCount: conv.unread_messages_count || 0,
    yourTurnLabel: !!conv.your_turn,
    userIsBlocked: !!conv.blocked,
    recentInboundMessageDate: conv.recent_inbound_message_date,
    facebookPost: conv.facebook_post || conv.facebookPost || null,
    platformSpecificData: conv.platform_specific_data || null,
    externalUserId: conv.external_user_id || conv.external_id || null,
    country: conv.country || null,
    aiCopilotsEnabled: !!options.aiCopilotsEnabled,
    leadScore: conv.lead_score ?? null,
    fep_active: !!(conv.fep_active || conv.fepActive),
    fepActive: !!(conv.fep_active || conv.fepActive),
    fep_window_expires_at: conv.fep_window_expires_at || conv.fepWindowExpiresAt || null,
    fepWindowExpiresAt: conv.fep_window_expires_at || conv.fepWindowExpiresAt || null,
    fep_expires_in_seconds: conv.fep_expires_in_seconds ?? conv.fepExpiresInSeconds ?? 0,
    fepExpiresInSeconds: conv.fep_expires_in_seconds ?? conv.fepExpiresInSeconds ?? 0
  };
};
